
import { FandfFormOfPayment, IFandfPensionInputs } from 'app/calcs/customers/fandf/interfaces'
import { ISharedPensionInputs } from 'app/calcs/customers/shared/interfaces'
import { IRetirementPerson, isValidPerson } from 'app/redux/Profile/model'
import { UserActions } from 'app/redux/User/actions'
import { theCensusInfo } from 'app/services/models/PublicCensusInfo';
import { IPokeandPullRequest, IRequestedValues, IWorkbookValues, WorkbookServiceResponse } from 'app/services/models/Workbook'
import { WorkbookService } from 'app/services/WorkbookService'
import * as utils from 'app/utils'
import { INosyParker, nullNosyParker } from 'app/utils/nosyParker'
import { getWebServicesConfig } from 'config'
import * as _ from 'lodash'
import * as moment from 'moment'

interface IFandfRunCalculationsRequest {
  member: IRetirementPerson
  spouse: IRetirementPerson
  vals: IFandfPensionInputs
  shared: ISharedPensionInputs
  when: moment.Moment
}

const sheetDateFormat = 'L'
const inputsSheetName = 'Input'
const outputsSheetName = 'Output FF'
const calculatorSheetName = 'Calculator_FF'
const formOfPaymentMap = [
  { form: FandfFormOfPayment.lifeOnly, range: 'c4:i43' },
  { form: FandfFormOfPayment.fiftyPercentJointAndSurvivor, range: 'm4:s43' },
  { form: FandfFormOfPayment.seventyFivePercentJointAndSurvivor, range: 'w4:ac43' },
  { form: FandfFormOfPayment.fullJointAndSurvivor, range: 'ae4:ak43' }
]

function makePaymentExtractor(vals: IFandfPensionInputs, when: moment.Moment) {
  return (wr: WorkbookServiceResponse): number => {
    if (_.isUndefined(vals.formOfPayment)) { return 0 }
    if (!wr.Results) { return 0 }
    const output = wr.Results[outputsSheetName]
    const tableKey = _.find(formOfPaymentMap, (q) => q.form === vals.formOfPayment)

    // find the table corresponding to the form of payment
    const table = output[tableKey.range]

    // find the row for the year we need
    let row = _.find(table, (r) => r[0] === when.year())
    if (!row) { row = _.first(table) }
    if (!row) {
      // tslint:disable-next-line:no-debugger
      debugger
      return 0
    }

    // some worksheets use 'financial' formatting which formats 0's as '-'
    if ('-' === row[6]) { row[6] = 0 }
    return row[6] || 0
  }
}

type FandfWorkbookDAOCalculator = (request: IFandfRunCalculationsRequest) => Promise<WorkbookServiceResponse>

class FandfWorkbookDAO {
  private service: WorkbookService

  constructor(protected nosyParker: INosyParker = nullNosyParker) {
    this.service = new WorkbookService(getWebServicesConfig().workbookEndpoint, 'fandfWorkbook')
  }

  /**
   * Run the Fandf calculations workbook.
   *
   * @param request The request parameters
   *
   * @returns a WorkbookServiceResponse.
   */
  runCalculations(request: IFandfRunCalculationsRequest): Promise<WorkbookServiceResponse> {

    // tslint:disable-next-line:no-console
    console.log('FandfWorkbookDAO using service: ' + this.service)

    this.nosyParker.operationIsStarting()
    const startTime = moment.utc()

    return this.issueRequest(request)
      .then((wr: WorkbookServiceResponse) => {
        const elapsed = moment.utc().diff(startTime)

        // tslint:disable-next-line:no-console
        console.log('It took ' + elapsed + 'ms for FandfWorkbookDAO calculations')

        if (wr.IsError || !wr.Results) {
          // tslint:disable-next-line:no-debugger
          //debugger
          this.nosyParker.operationHasFailed(wr)
        } else {
          this.nosyParker.operationHasCompleted(wr)
        }

        return wr
      })
      .catch((reason) => {
        this.nosyParker.operationHasFailed(reason)
        return Promise.reject(reason)
      })
  }

  issueRequest(request: IFandfRunCalculationsRequest): Promise<WorkbookServiceResponse> {
    const spousePresent = isValidPerson(request.spouse)
    const spouseDOB = spousePresent ? request.spouse.dob.format(sheetDateFormat) : ''
    const spouseLifeExpectancy = spousePresent ? request.spouse.selectedAgeExpectancy : ''

    const inputs: IWorkbookValues = {
      [inputsSheetName]: {
        'c4': theCensusInfo.censusDate.format(sheetDateFormat),
        'c10:c15': [
            [request.member.dob.format(sheetDateFormat)],
            [request.vals.hireDate.format(sheetDateFormat)],
            [request.member.gender],
            [spouseDOB],
            [request.member.selectedAgeExpectancy],
            [spouseLifeExpectancy]
        ],
        'c18:c20': [
            [request.vals.beginPaymentsAge.years()],
            [request.vals.basePay],
            [request.vals.annualSalaryIncrease / 100.0 ]
        ]
      }
    }

    const outputs: IRequestedValues = {
      [outputsSheetName]: _.map(formOfPaymentMap, (fop) => fop.range),
      [calculatorSheetName]: ['a1:j40']
    }

    const req: IPokeandPullRequest = { inputs, outputs }
    return this.service.pokeAndPull(UserActions.getSharedVar('accessToken'), req)
  }

}

export {
  IFandfRunCalculationsRequest,
  FandfWorkbookDAO,
  FandfWorkbookDAOCalculator,

  makePaymentExtractor
}



// WEBPACK FOOTER //
// ./src/app/workbookDAOs/fandfDAO/index.ts